import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailControlSystem } from '../../dto/trail-control-system';
import { TrailControlSystemService } from '../../service/trail-control-system.service';
import { Trail } from '../../dto/trail';
import { TrailControlSystemDetailComponent } from '../trail-control-system-detail/trail-control-system-detail.component';

@Component({
  selector: 'dpi-trail-control-system',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule,
    MatTooltipModule, NgIf],
  templateUrl: './trail-control-system.component.html',
  styleUrl: './trail-control-system.component.scss'
})
export class TrailControlSystemComponent implements OnInit, OnDestroy {
  trail: Trail;
  displayedColumns: string[] = ['name', 'maintenance_checked', 'last_software_version', 'remark'];
  dataSource: TrailControlSystem[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailControlSystemService: TrailControlSystemService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model);
      })

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailControlSystemService.getAllSetupOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
          for (var obj of this.dataSource) {
            if (obj.maintenance_checked) {
              obj.maintenance_check_text = 'Yes';
            } else {
              obj.maintenance_check_text = 'No';
            }
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectTrailControlSystem(selected: TrailControlSystem) {
    if (!this.isReadonly) {
      var objCopy: TrailControlSystem = { ...selected };
      const dialogRef = this._dialog.open(TrailControlSystemDetailComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.trailControlSystem);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.trailControlSystem.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  create() {
    this.trailControlSystemService.createControlSystem(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailControlSystem(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailControlSystem) {
    let list: TrailControlSystem[] = [];
    for (var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  update(model: TrailControlSystem) {
    this.trailControlSystemService.updateControlSystem(model)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.replaceModelInDataSource(res);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailControlSystem) {
    let list: TrailControlSystem[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        if (model.maintenance_checked) {
          model.maintenance_check_text = 'Yes';
        } else {
          model.maintenance_check_text = 'No';
        }
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  delete(modelId: number) {
    this.trailControlSystemService.deleteControlSystem(modelId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(modelId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(modelId: number) {
    let list: TrailControlSystem[] = [];
    for (var item of this.dataSource) {
      if (item.id === modelId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }


}

